import classNames from 'classnames';
import React, { useContext } from 'react';
import styled from 'styled-components';

import { Button } from '../../components/Button';
import { AuthenticatorContext } from './AuthenticatorContext';

export const Form = styled.form`
   h1 {
      font-family: var(--heading-font);
      font-size: 1.4em;
      margin-bottom: 1.5em;
      text-align: center;
   }

   input {
      width: 100%;
      border-radius: 5px;
      padding: 10px;
      border: 2px solid;
      color: var(--text-color);
      font-weight: bold;
      font-size: 1.1em;
      background-color: var(--brand-white);
      outline: none;
      margin-bottom: 0.5em;
   }

   input:disabled,
   input:read-only {
      opacity: 0.66;
      background-color: #dfdfdf;
      cursor: not-allowed;
   }

   input::placeholder {
      color: var(--brand-darker-green);
      opacity: 0.5;
   }

   @media (min-width: 500px) {
      input {
         width: 100%;
      }
   }

   input:not(:disabled, :read-only):hover,
   input:not(:disabled, :read-only):focus,
   input:not(:disabled, :read-only):active {
      border-color: var(--hover-color);
   }

   @media (prefers-color-scheme: dark) {
      input {
         background-color: var(--brand-lighter-green);
         color: var(--brand-darker-green);
         border-color: var(--brand-green);
      }

      input::placeholder {
         color: var(--brand-darker-green);
         opacity: 0.5;
      }
   }

   p {
      font-family: var(--content-font);
      margin-bottom: 1em;
      line-height: 1.5;
   }

   .error-message {
      font-weight: bold;
      color: var(--semantic-error-interact);
   }

   .success-message {
      font-weight: bold;
      color: var(--semantic-success-interact);
   }

   @media (prefers-color-scheme: dark) {
      .success-message {
         color: var(--semantic-success);
      }

      .error-message {
         color: var(--semantic-error);
         color: #e43a48;
      }
   }

   .button-cta {
      width: 100%;
      margin-bottom: 2.5em;
      margin-top: 1.5em;
   }

   .button-forgot-password {
      padding: 0;
   }
`;

const AuthenticatorMessage = () => {
   const {
      state: { errorMessage, successMessage },
   } = useContext(AuthenticatorContext);

   if (errorMessage == null && successMessage == null) {
      return null;
   }

   return (
      <p className={`${errorMessage ? 'error' : 'success'}-message`}>
         {errorMessage || successMessage}
      </p>
   );
};

const AuthenticatorForm = ({ children, onSubmit, heading }) => {
   const { dispatch } = useContext(AuthenticatorContext);
   return (
      <Form
         className="selectable"
         onSubmit={(e) => {
            e.preventDefault();
            dispatch({
               type: 'submitForm',
            });
            onSubmit(new FormData(e.target)).catch((err) => {
               dispatch({
                  type: 'setMessage',
                  errorMessage: err?.message || 'An unexpected error ocurred.',
               });
            });
         }}
      >
         <h1>{heading}</h1>
         <AuthenticatorMessage />
         {children}
      </Form>
   );
};

const AuthenticatorSubmitButton = ({ children }) => {
   const {
      state: { formSubmitting },
   } = useContext(AuthenticatorContext);

   return (
      <Button
         disabled={formSubmitting}
         className={classNames('button-cta', {
            'button-loading': formSubmitting,
         })}
         type="submit"
      >
         {children}
      </Button>
   );
};

const AuthenticatorButton = ({ children, disabled, ...props }) => {
   const {
      state: { formSubmitting },
   } = useContext(AuthenticatorContext);

   return (
      <Button disabled={disabled || formSubmitting} {...props} type="button">
         {children}
      </Button>
   );
};

const AuthenticatorInput = (props) => {
   const {
      state: { formSubmitting },
   } = useContext(AuthenticatorContext);

   return <input disabled={formSubmitting} {...props} />;
};

export {
   AuthenticatorForm,
   AuthenticatorInput,
   AuthenticatorSubmitButton,
   AuthenticatorButton,
};
